import { Expense, ExpenseStatusEnum, Hospitality, InboxInvoice, ItemSplit, Mileage, PerDiem, RightEnum, SplitTypeEnum, TripFolder } from "@finway-group/shared/lib/models"
import { roundNumberTo2Decimals, toAmount, toDinero } from "@finway-group/shared/lib/utils"
import { calculateNetPriceFromGrossPriceAndTaxRate } from "@finway-group/shared/lib/utils/expense.utils"
import { FormInstance } from "antd/lib/form"
import { useEffect } from "react"
import { useSelector } from "react-redux"
import { useDebouncedCallback } from "use-debounce/lib"

import { MenuInfo } from "Components/form/formAddExpenseItemRow"
import { ExpenseData, Filler } from "Components/modals/expenseCreateForm.context"
import ExpenseInitializer from "Features/pages/expenses/expenseInitializer"
import {
    PriceSourceEnum,
    determineExpensePrice,
    getExpenseModelByKind,
    getTableAndAdjacentExpenseLabel,
    syncTotalExpenseAmountsToItems,
} from "Features/pages/expenses/expenseUtils"
import { DEBOUNCE_DURATION_LONG, FINWAY_ADMIN_EMAIL, NO_TAX_RATE } from "Shared/config/consts"
import { useTaxes } from "Shared/hooks/tax.hooks"
import { ExpenseService } from "Shared/services"
import { ExpenseCountInterface } from "Shared/store/actions/expense/expenseTypes"
import { RootState } from "Shared/store/rootState.interface"
import { addPrices, getTax, isRightGranted, subtractPrices } from "Shared/utils/helpers"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"

import { useCostCenters } from "./costCenter.hooks"
import { useEmployees } from "./employee.hooks"
import { useRolesMap } from "./role.hooks"
import { useActiveCompanyProfile } from "./user.hooks"
import { useVendors } from "./vendor.hooks"

export const useExpenseCounts = (): ExpenseCountInterface => useSelector(({ expenses }: RootState) => expenses.counts)

export const useInboxInvoicesCount = () => useSelector(({ inboxInvoices }: RootState) => inboxInvoices.count)

export const useShouldRefetchExpenses = () => useSelector(({ expenses }: RootState) => expenses.shouldRefetch)

export const useShouldRefetchInbox = () => useSelector(({ inboxInvoices }: RootState) => inboxInvoices.shouldRefetch)

export const useExpenses = (excludeDeleted: boolean = false) => {
    const { items, totalPages, totalDocs, counts } = useSelector(({ expenses }: RootState) => expenses)
    let expenses = items.map((expense: Expense) => new Expense(expense))

    if (excludeDeleted) {
        expenses = expenses?.filter((expense: Expense) => !expense.deleted)
    }

    return {
        expenses,
        totalPages,
        totalDocs,
        counts,
    }
}

export const useInboxInvoices = () => {
    const { items, totalPages, totalDocs, count } = useSelector(({ inboxInvoices }: RootState) => inboxInvoices)
    const inboxInvoices = items.map((i: InboxInvoice) => new InboxInvoice(i))

    return {
        inboxInvoices,
        totalPages,
        totalDocs,
        count,
    }
}

export const useSubscriptions = (excludeDeleted: boolean = false) => {
    const { expenses } = useExpenses(excludeDeleted)
    return expenses.filter((expense: Expense) => expense.isSubscription() && expense.isInitialSubscription())
}

export const useExpense = () => {
    const expenseState = useSelector(({ expenses }: RootState) => expenses.item)
    const ExpenseModel = getExpenseModelByKind(expenseState.kind)

    const expense = new ExpenseModel(expenseState)

    return expense
}

export const useActiveTab = () => useSelector(({ expenses }: RootState) => expenses.activeTab)

export const useExpenseSwitching = (expenseId: string, expenseStatus: ExpenseStatusEnum, requestedBy: string) => {
    const [adjacentExpenses, setAdjacentExpenses] = useStateIfMounted({ prevExpense: "", nextExpense: "" })
    const [isLoading, setIsLoading] = useStateIfMounted(false)
    const [hasError, setHasError] = useStateIfMounted(false)
    const [table, adjacentExpenseLabel] = getTableAndAdjacentExpenseLabel(expenseStatus, requestedBy)

    const { prevExpense, nextExpense } = adjacentExpenses

    useEffect(() => {
        if (expenseId) {
            setIsLoading(true)
            ExpenseService.getAdjacentExpenses(expenseId, table)
                .then(({ prev, next }: { prev: string; next: string }) => setAdjacentExpenses({ prevExpense: prev, nextExpense: next }))
                .catch(() => setHasError(true))
                .finally(() => setIsLoading(false))
        }
    }, [expenseId])

    return { prevExpense, nextExpense, isExpenseSwitchingLoading: isLoading, hasError, adjacentExpenseLabel }
}

export const useRequestActionDisabled = () => {
    const employees = useEmployees({ excludeDeleted: true })
    const vendors = useVendors(true)
    const costCenters = useCostCenters(true)
    const rolesMap = useRolesMap({ excludeDeleted: true })
    const activeCompanyProfile = useActiveCompanyProfile()

    const filteredEmployees = employees.filter((employeeToFilter) => {
        const role = rolesMap.get(employeeToFilter.activeCompanyProfile?.roleId)
        return (
            employeeToFilter.activeCompanyProfile &&
            !employeeToFilter.activeCompanyProfile.deleted &&
            ((isRightGranted(role?.rights, RightEnum.EXPENSE__ALL__APPROVE) && employeeToFilter.email !== FINWAY_ADMIN_EMAIL) ||
                (isRightGranted(role?.rights, RightEnum.EXPENSE__TEAM__APPROVE) && employeeToFilter.activeCompanyProfile.team === activeCompanyProfile.team))
        )
    })

    return vendors.length === 0 || costCenters.length === 0 || filteredEmployees.length === 0
}

type UpdateClosure<T> = (split: ItemSplit) => T

export const useExpenseHook = (
    expense: Expense,
    expenseForm: FormInstance<Partial<Expense<string> | PerDiem | Hospitality | Mileage | TripFolder> | Filler>,
    updateExpense: (data: ExpenseData) => void,
    isLinked: boolean,
    setIsLinked: (linked: boolean) => void,
    isForcedSync?: boolean,
) => {
    const taxValues = useTaxes()
    // when isForcedSync is true, where changes in the items MUST affect the total amounts to keep them synced at all times.
    const [shouldRecalculateAmounts, setShouldRecalculateAmounts] = useStateIfMounted(false)

    const recalculateAmountsCallback = useDebouncedCallback(() => setShouldRecalculateAmounts(true), 200)

    const onAddItem = (addItemAtTheBeginning = false) => {
        const taxValueArray = ExpenseInitializer.initializeNewItemSplit(taxValues)

        updateExpense({
            splits: addItemAtTheBeginning ? [taxValueArray, ...expense.splits] : [...expense.splits, taxValueArray],
            splitType: SplitTypeEnum.ITEM,
        })
    }

    const onSelect = ({ key, domEvent }: MenuInfo): void => {
        domEvent.stopPropagation()
        switch (key) {
            case "add_item":
                onAddItem()
                break
            case "delete_all_items":
                updateExpense({ splits: [] })
                break
            case "link":
                setIsLinked(!isLinked)
                break
            default:
                break
        }
    }

    const applyUpdateClosureAtIndex = <T>(split: ItemSplit, index: number, targetIndex: number, updateClosure: UpdateClosure<T>): ItemSplit =>
        index === targetIndex ? { ...split, ...updateClosure(split) } : split

    const updateItemSplit = <T>(index: number, updateClosure: UpdateClosure<T>): void => {
        const splits = expenseForm.getFieldValue("splits")
        const updatedSplits = splits.map((split: ItemSplit, i: number) => applyUpdateClosureAtIndex(split, i, index, updateClosure))

        updateExpense({ splits: updatedSplits })

        if (isForcedSync && expense.splits?.length > 0 && expense.splitType === SplitTypeEnum.ITEM) {
            recalculateAmountsCallback.callback()
        }
    }

    const onNameChange = useDebouncedCallback((name: string, index: number) => {
        updateItemSplit(index, () => ({ name }))
    }, DEBOUNCE_DURATION_LONG)

    const onTotalNetPriceUpdate = useDebouncedCallback((totalNetPrice: number) => {
        const { taxRate } = expense
        const dineroTotalNet = toDinero(totalNetPrice)
        const totalTaxPrice = taxRate && taxRate?._id !== NO_TAX_RATE ? toAmount(dineroTotalNet.percentage(getTax(taxValues, taxRate?._id))) : expense.totalTaxPrice
        const totalGrossPrice = toAmount(dineroTotalNet.add(toDinero(totalTaxPrice)))
        updateExpense({
            totalNetPrice,
            ...(isLinked ? { totalGrossPrice, totalTaxPrice } : {}),
        })
    }, DEBOUNCE_DURATION_LONG)

    const onTaxRateUpdate = (taxRateId: string, priceSource: PriceSourceEnum) => {
        if (taxRateId === NO_TAX_RATE) {
            updateExpense({ taxRate: { _id: NO_TAX_RATE } })
            return
        }

        const taxRate = getTax(taxValues, taxRateId)

        const {
            taxPrice: totalTaxPrice,
            grossPrice: totalGrossPrice,
            netPrice: totalNetPrice,
        } = determineExpensePrice(taxRate, priceSource === PriceSourceEnum.NET_PRICE ? expense.totalNetPrice : expense.totalGrossPrice, priceSource)

        updateExpense({
            taxRate: { _id: taxRateId },
            ...(isLinked ? { totalTaxPrice, totalGrossPrice, totalNetPrice } : {}),
        })
    }

    const onTaxRateSplitUpdate = (index: number, taxRateId: string, priceSource: PriceSourceEnum) => {
        // Note: for general tax rate update after split tax rate update, see getCommonTaxRateAcrossItems
        updateItemSplit(index, (itemSplit: ItemSplit) => {
            if (taxRateId === NO_TAX_RATE) return { taxRate: { _id: NO_TAX_RATE } }
            const taxRate = getTax(taxValues, taxRateId)

            const { taxPrice, grossPrice, netPrice } = determineExpensePrice(
                taxRate,
                priceSource === PriceSourceEnum.NET_PRICE ? itemSplit.netPrice : itemSplit.grossPrice,
                priceSource,
            )

            return {
                taxRate: { _id: taxRateId },
                ...(isLinked ? { taxPrice, grossPrice, netPrice } : {}),
            }
        })
    }

    const onTotalTaxPriceUpdate = (totalTaxPrice: number, priceSource: PriceSourceEnum) => {
        // we unset the taxRate when we manually update the total tax price
        if (!isLinked) {
            updateExpense({ totalTaxPrice })
            return
        }

        const updateObject = { totalTaxPrice, taxRate: { _id: NO_TAX_RATE } }

        switch (priceSource) {
            case PriceSourceEnum.GROSS_PRICE:
                updateExpense({ ...updateObject, totalNetPrice: subtractPrices(expense.totalGrossPrice, [totalTaxPrice]) })
                break
            case PriceSourceEnum.NET_PRICE:
                updateExpense({ ...updateObject, totalGrossPrice: addPrices([expense.totalNetPrice, totalTaxPrice]) })
                break
            default:
                break
        }
    }

    const onTotalGrossPriceUpdate = useDebouncedCallback((totalGrossPrice: number) => {
        if (!isLinked) {
            updateExpense({ totalGrossPrice })
            return
        }

        if (expense.taxRate && expense.taxRate?._id !== NO_TAX_RATE) {
            const tax = getTax(taxValues, expense.taxRate?._id)
            const totalNetPrice = calculateNetPriceFromGrossPriceAndTaxRate(totalGrossPrice, tax)

            updateExpense({
                totalNetPrice,
                totalTaxPrice: toAmount(toDinero(totalNetPrice).percentage(tax)),
                totalGrossPrice,
            })
        } else {
            const netPriceDifference = totalGrossPrice - expense.totalTaxPrice
            const totalNetPrice = netPriceDifference < 0 ? 0 : roundNumberTo2Decimals(netPriceDifference)
            const totalTaxPrice = netPriceDifference < 0 ? 0 : expense.totalTaxPrice
            updateExpense({ totalNetPrice, totalGrossPrice, totalTaxPrice })
        }
    }, DEBOUNCE_DURATION_LONG)

    const calculateTaxPrice = ({ taxRate, taxPrice }: ItemSplit, netPrice: number) => {
        if (taxRate?._id !== NO_TAX_RATE) {
            return toAmount(toDinero(netPrice).percentage(getTax(taxValues, taxRate?._id!)))
        }
        return toAmount(toDinero(taxPrice))
    }

    const onNetPriceUpdate = (index: number, netPrice: number) => {
        updateItemSplit(index, (itemSplit) => {
            const taxPrice = calculateTaxPrice(itemSplit, netPrice)

            const updatedItemSplit = {
                netPrice,
                ...(isLinked
                    ? {
                          grossPrice: toAmount(toDinero(netPrice).add(toDinero(taxPrice))),
                          taxPrice,
                      }
                    : {}),
            }

            return updatedItemSplit
        })
    }

    const onTaxPriceUpdate = (index: number, taxPrice: number, priceSource: PriceSourceEnum) => {
        // we unset the taxRate when we manually update the total tax price
        if (!isLinked) {
            updateItemSplit(index, (_itemSplit: ItemSplit) => ({ taxPrice }))
            return
        }

        const updateObject = {
            taxPrice,
            taxRate: { _id: NO_TAX_RATE },
        }

        switch (priceSource) {
            case PriceSourceEnum.NET_PRICE:
                updateItemSplit(index, (itemSplit: ItemSplit) => ({
                    ...updateObject,
                    grossPrice: addPrices([itemSplit.netPrice, taxPrice]),
                }))
                break
            case PriceSourceEnum.GROSS_PRICE:
                updateItemSplit(index, (itemSplit: ItemSplit) => ({
                    ...updateObject,
                    netPrice: subtractPrices(itemSplit.grossPrice, [taxPrice]),
                }))
                break
            default:
                break
        }
    }

    const onGrossPriceUpdate = (index: number, grossPrice: number) => {
        updateItemSplit(index, ({ taxRate, taxPrice }: ItemSplit) => {
            if (!isLinked) return { grossPrice }

            if (taxRate?._id !== NO_TAX_RATE) {
                const tax = getTax(taxValues, taxRate?._id!)
                const netPrice = calculateNetPriceFromGrossPriceAndTaxRate(grossPrice, tax)

                return {
                    taxPrice: toAmount(toDinero(netPrice).percentage(tax)),
                    netPrice,
                    grossPrice,
                }
            }

            const netPriceDifference = toAmount(toDinero(grossPrice).subtract(toDinero(taxPrice)))

            if (netPriceDifference <= 0) return { netPrice: 0, taxPrice: 0, grossPrice }

            return { netPriceDifference, taxPrice, grossPrice }
        })
    }

    useEffect(() => {
        if (shouldRecalculateAmounts) {
            updateExpense(syncTotalExpenseAmountsToItems(expense.splits))
            setShouldRecalculateAmounts(false)
        }
    }, [shouldRecalculateAmounts])

    return {
        onAddItem,
        onSelect,
        onTaxRateUpdate,
        onTaxPriceUpdate,
        onNetPriceUpdate,
        onTaxRateSplitUpdate,
        onTotalNetPriceUpdate,
        onTotalTaxPriceUpdate,
        onTotalGrossPriceUpdate,
        onGrossPriceUpdate,
        onNameChange,
        setShouldRecalculateAmounts,
    }
}
